import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/auth/auth-provider";
import { createReportDraftLink, createReportLink, reportOrganizationMatches } from "@/lib/report-links";
import { reportingAdmin } from "@/lib/reporting";
import { GrafanaEmbedFrame } from "@/components/grafana-embed";
import { PageHeader } from "@/components/page-header";
import { ReportScopeFilters } from "@/components/report-scope-filters";
import { ReportExplainer } from "@/components/report-explainer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type AnalyticsFeeder, type GrafanaReportFilters } from "@/lib/api";
import { GRAFANA_EMBED_QUERY_OPTIONS } from "@/lib/grafana-embed-query";
import {
  DEFAULT_DASHBOARD_RANGE,
  MAX_LOOKBACK_MS,
  RETENTION_HELP,
  dashboardRangeError,
  dashboardTime,
  lagosInputValue,
} from "@/lib/dashboard-time";
import {
  REPORTS,
  REPORT_RANGES,
  normalizeReportFilters,
  readReportFilters,
  reportSearchParams,
  visibleReportFeeders,
  type ActiveGrafanaReport,
} from "@/lib/report-filters";

export function ReportPage({ report }: { report: ActiveGrafanaReport }): JSX.Element {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const organizationId = me?.tenant_id ?? null;
  const organizationMatches = reportOrganizationMatches(search, organizationId);
  const scopedOrganization = search.has("organization_id") && organizationMatches
    ? organizationId ?? undefined : undefined;
  const [copyStatus, setCopyStatus] = useState("");
  // Legacy navigation keeps its existing range normalization. Shared app links
  // must never silently replace a fixed period with the default relative range.
  const rangeError = search.has("organization_id")
    ? dashboardRangeError(
        search.get("from") ?? DEFAULT_DASHBOARD_RANGE.from,
        search.get("to") ?? DEFAULT_DASHBOARD_RANGE.to,
      )
    : null;
  const catalogue = useQuery({
    queryKey: ["analytics-feeders", organizationId, ...(scopedOrganization === undefined ? [] : ["scoped"])],
    queryFn: ({ signal }) => scopedOrganization === undefined
      ? api.analyticsFeeders(signal) : api.analyticsFeeders(signal, scopedOrganization),
    enabled: organizationMatches,
  });
  const requestedFilters: GrafanaReportFilters = readReportFilters(search);
  const availableFeeders: readonly AnalyticsFeeder[] = catalogue.data ?? [];
  // Find a station before Performance selects its required default feeder.
  // Otherwise the catalog's first feeder would decide the station, not its name.
  const initialScope: GrafanaReportFilters = normalizeReportFilters(requestedFilters, availableFeeders, "executive");
  const defaultSubstation: string | undefined = search.has("substation")
    ? undefined
    : Array.from(new Set(
        visibleReportFeeders(availableFeeders, initialScope)
          .map((feeder) => feeder.substation).filter(Boolean),
      )).sort((a, b) => a.localeCompare(b))[0];
  const filters: GrafanaReportFilters = normalizeReportFilters(
    defaultSubstation ? { ...requestedFilters, substation: defaultSubstation } : requestedFilters,
    availableFeeders,
    report,
  );
  const normalizedSearch: string = reportSearchParams(filters, search).toString();
  const currentSearch: string = search.toString();
  const visibleFeeders: AnalyticsFeeder[] = visibleReportFeeders(catalogue.data ?? [], filters);
  const title: string =
    REPORTS.find((definition) => definition.report === report)?.title ?? "Analytics";

  useEffect(() => {
    if (
      organizationMatches && !rangeError && catalogue.isSuccess &&
      catalogue.data.length > 0 && normalizedSearch !== currentSearch
    )
      setSearch(normalizedSearch, { replace: true });
  }, [organizationMatches, rangeError, catalogue.isSuccess, catalogue.data, normalizedSearch, currentSearch, setSearch]);

  const embed = useQuery({
    queryKey: ["grafana-report", organizationId, report, filters, ...(scopedOrganization === undefined ? [] : ["scoped"])],
    queryFn: ({ signal }) => scopedOrganization === undefined
      ? api.grafanaReportEmbed(report, filters, signal)
      : api.grafanaReportEmbed(report, filters, signal, scopedOrganization),
    enabled:
      organizationMatches && !rangeError && catalogue.isSuccess &&
      filters.program !== "" && visibleFeeders.length > 0,
    ...GRAFANA_EMBED_QUERY_OPTIONS,
  });

  async function copyReportLink(): Promise<void> {
    setCopyStatus("");
    try {
      if (
        !organizationMatches || !organizationId || !catalogue.isSuccess ||
        !visibleFeeders.length || rangeError
      )
        throw new Error("Report is not ready to share.");
      const link = createReportLink(report, filters, organizationId, window.location.origin);
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable.");
      await navigator.clipboard.writeText(link);
      setCopyStatus("Report link copied.");
    } catch {
      setCopyStatus("Could not copy the report link. Check clipboard permissions and try again.");
    }
  }

  function prepareExport(workspace: "exports" | "schedules"): void {
    try {
      if (!organizationMatches || !organizationId || !catalogue.isSuccess || rangeError ||
        (workspace === "schedules" && !reportingAdmin(me))) throw new Error("Report is not ready.");
      navigate(createReportDraftLink(workspace, report, filters, organizationId, catalogue.data));
    } catch (error) {
      setCopyStatus(error instanceof Error ? error.message : "Could not prepare draft.");
    }
  }

  function updateFilters(next: GrafanaReportFilters): void {
    setSearch(
      reportSearchParams(normalizeReportFilters(next, catalogue.data ?? [], report), search),
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        description="Substation analytics with adjustable scope and time filters."
        actions={
          <>
            <Button
              variant="outline"
              disabled={!organizationMatches || !organizationId || !catalogue.isSuccess || !visibleFeeders.length || !!rangeError}
              onClick={() => void copyReportLink()}
            >
              Copy report link
            </Button>
            <Button variant="outline"
              disabled={!organizationMatches || !organizationId || !catalogue.isSuccess || !visibleFeeders.length || !!rangeError}
              onClick={() => prepareExport("exports")}>
              {report === "executive" ? "Prepare report export" : "Prepare CSV export"}
            </Button>
            {reportingAdmin(me) ? <Button variant="outline"
              disabled={!organizationMatches || !organizationId || !catalogue.isSuccess || !visibleFeeders.length || !!rangeError}
              onClick={() => prepareExport("schedules")}>
              {report === "executive" ? "Prepare scheduled report" : "Prepare scheduled CSV + report link"}
            </Button> : null}
            <ReportExplainer report={report} />
          </>
        }
      />
      <p className="mb-3 text-xs text-muted-foreground">
        PNG unavailable. Share an app link, not an image; recipients need app access.
      </p>
      {copyStatus ? <p role="status">{copyStatus}</p> : null}
      {!organizationMatches ? (
        <p role="alert">This link requests a different or invalid organization. Use the account organization selector to switch to the intended organization if you have access, then reopen this link. No organization is switched automatically.</p>
      ) : rangeError ? <p role="alert">{rangeError}</p> : null}
      <section
        aria-label="Report workspace"
        className="grid min-w-0 gap-4 lg:grid-cols-[14rem_minmax(0,1fr)]"
      >
        <aside
          aria-label="Report filters"
          className="min-w-0 self-start rounded-lg border border-border bg-card p-3 lg:sticky lg:top-0"
        >
          <h2 className="mb-3 text-sm font-semibold">Filters</h2>
          {catalogue.isLoading ? <Skeleton className="h-48 w-full" /> : null}
          {catalogue.error ? <ErrorBanner error={catalogue.error} /> : null}
          {catalogue.data && catalogue.data.length > 0 ? (
            <ReportFilters
              report={report}
              filters={filters}
              feeders={catalogue.data}
              onChange={updateFilters}
            />
          ) : null}
        </aside>
        <section aria-label="Report content" className="min-w-0">
          {catalogue.isSuccess && catalogue.data.length === 0 ? (
            <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
              No monitored feeders are available in your current organization.
            </div>
          ) : null}
          {catalogue.isSuccess && catalogue.data.length > 0 && visibleFeeders.length === 0 ? (
            <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
              No authorized feeders match these filters. Choose an available filter value; unknown
              or outdated labels are not automatically broadened to All.
            </div>
          ) : null}
          {embed.error ? <ErrorBanner error={embed.error} /> : null}
          {organizationMatches && !rangeError && visibleFeeders.length > 0 && !embed.error && !embed.isFetchedAfterMount ? (
            <Skeleton className="h-[calc(100vh-18rem)] min-h-[720px] w-full" />
          ) : null}
          {organizationMatches && !rangeError && visibleFeeders.length > 0 && !embed.error && embed.isFetchedAfterMount && embed.data ? (
            <GrafanaEmbedFrame
              key={`${organizationId}:${report}:${normalizedSearch}`}
              url={embed.data.url}
              title={`${title} dashboard`}
              onRetry={() => void embed.refetch()}
            />
          ) : null}
        </section>
      </section>
    </>
  );
}

const CONTROL_CLASS =
  "box-border block h-9 w-full min-w-0 max-w-full truncate rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function ReportFilters({
  report,
  filters,
  feeders,
  onChange,
}: {
  report: ActiveGrafanaReport;
  filters: GrafanaReportFilters;
  feeders: readonly AnalyticsFeeder[];
  onChange: (filters: GrafanaReportFilters) => void;
}): JSX.Element {
  return (
    <div className="grid min-w-0 grid-cols-1 gap-2">
      <ReportScopeFilters report={report} filters={filters} feeders={feeders} onChange={onChange} />
      {report === "executive" ? (
        <label className="space-y-1 text-xs font-medium text-muted-foreground">
          <span>Group by</span>
          <select
            aria-label="Group by"
            className={CONTROL_CLASS}
            value={filters.groupby}
            onChange={(event) =>
              onChange({
                ...filters,
                groupby: event.target.value as GrafanaReportFilters["groupby"],
              })
            }
          >
            <option value="Overall">Overall</option>
            <option value="DISCO">DisCo</option>
            <option value="Substation">Substation</option>
          </select>
        </label>
      ) : null}
      <TimeRange key={`${filters.from}:${filters.to}`} filters={filters} onChange={onChange} />
    </div>
  );
}

function TimeRange({
  filters,
  onChange,
}: {
  filters: GrafanaReportFilters;
  onChange: (filters: GrafanaReportFilters) => void;
}): JSX.Element {
  const selectedPreset: string =
    REPORT_RANGES.find((range) => range.from === filters.from && range.to === filters.to)?.label ??
    "Custom";
  const [custom, setCustom] = useState<boolean>(selectedPreset === "Custom");
  const now = Date.now();
  const [start, setStart] = useState<string>(
    lagosInputValue(dashboardTime(filters.from, false, now)),
  );
  const [end, setEnd] = useState<string>(lagosInputValue(dashboardTime(filters.to, true, now)));
  const [error, setError] = useState<string>("");

  function applyRange(): void {
    const from: number = Date.parse(`${start}+01:00`);
    const to: number = Date.parse(`${end}+01:00`);
    const rangeError = dashboardRangeError(String(from), String(to));
    if (rangeError) {
      setError(rangeError);
      return;
    }
    onChange({ ...filters, from: String(from), to: String(to) });
  }

  return (
    <div className="space-y-2">
      <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
        <span>Time range (Africa/Lagos)</span>
        <select
          aria-label="Time range"
          value={custom ? "Custom" : selectedPreset}
          className={CONTROL_CLASS}
          onChange={(event) => {
            const range = REPORT_RANGES.find((preset) => preset.label === event.target.value);
            setCustom(!range);
            if (range) onChange({ ...filters, from: range.from, to: range.to });
          }}
        >
          {REPORT_RANGES.map((range) => (
            <option key={range.label}>{range.label}</option>
          ))}
          <option>Custom</option>
        </select>
      </label>
      {custom ? (
        <div className="flex flex-col gap-2">
          <label className="min-w-0 flex-1 space-y-1 text-xs text-muted-foreground">
            <span>From (Lagos)</span>
            <input
              type="datetime-local"
              aria-label="From (Lagos)"
              min={lagosInputValue(Math.ceil((now - MAX_LOOKBACK_MS) / 60000) * 60000)}
              max={lagosInputValue(now)}
              className={CONTROL_CLASS}
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </label>
          <label className="min-w-0 flex-1 space-y-1 text-xs text-muted-foreground">
            <span>To (Lagos)</span>
            <input
              type="datetime-local"
              aria-label="To (Lagos)"
              min={lagosInputValue(Math.ceil((now - MAX_LOOKBACK_MS) / 60000) * 60000)}
              max={lagosInputValue(now)}
              className={CONTROL_CLASS}
              value={end}
              onChange={(event) => setEnd(event.target.value)}
            />
          </label>
          <Button variant="outline" onClick={applyRange}>
            Apply range
          </Button>
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">{RETENTION_HELP}</p>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  return (
    <div
      role="alert"
      className="mb-6 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      {error instanceof Error ? error.message : "Failed to load the report"}
    </div>
  );
}
